import { useEffect } from "react";
import { MiniAppLoginModalSWMSheet } from "@components/modals/MiniAppLoginModalSWM";
import { useFarcasterContext } from "@hooks/useFarcasterContext";
import { useAuthStore } from "@utils/firebaseAuth/auth";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";

// Module-level flag to track if the prompt has been shown this session
// Persists across component unmount/remount cycles
let hasShownPrompt = false;

/**
 * Shows a login prompt when users open the app in a Farcaster mini app context
 * and are not logged in. Only shows once per session.
 */
const MiniAppLoginPrompt = () => {
  const { isInMiniApp, isLoading: isFarcasterLoading } = useFarcasterContext();
  const authStatus = useAuthStore((state) => state.status);
  const bottomSheetManager = useBottomSheetManager();

  useEffect(() => {
    // Don't show if we've already shown it this session
    if (hasShownPrompt) {
      return;
    }

    // Wait for both Farcaster and auth checks to complete
    if (
      isFarcasterLoading ||
      authStatus === "firebase-auth-pending" ||
      authStatus === "db-user-pending"
    ) {
      return;
    }

    // Only show in mini app when user is not logged in
    if (isInMiniApp && authStatus === "no-user") {
      // Small delay to ensure UI is ready
      const timeoutId = setTimeout(() => {
        bottomSheetManager.open(<MiniAppLoginModalSWMSheet />, {
          mode: "push",
        });
        hasShownPrompt = true;
      }, 500);

      // Cleanup: clear timeout if component unmounts or dependencies change
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [authStatus, bottomSheetManager, isFarcasterLoading, isInMiniApp]);

  return null;
};

export default MiniAppLoginPrompt;
